/**
 * Redux slice for posting remaining unfilled shifts to broad gig teams.
 *
 * Wraps POST /api/gig/postUnfilled/{siteCd}. Backend is a stub today —
 * returns { message, timestamp }.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { GigPostUnfilledResponse } from '../../types/gigApi';

interface GigPostUnfilledState {
    lastSiteCd: string | null;
    lastResponse: GigPostUnfilledResponse | null;
    loading: boolean;
    success: boolean;
    error: string | null;
}

const initialState: GigPostUnfilledState = {
    lastSiteCd: null,
    lastResponse: null,
    loading: false,
    success: false,
    error: null,
};

/**
 * Post remaining unfilled shifts to broad gig teams for a site.
 * Uses: POST /api/gig/postUnfilled/{siteCd}
 */
export const postGigUnfilled = createAsyncThunk(
    'gigPostUnfilled/post',
    async ({ siteCd }: { siteCd: string }, { rejectWithValue }) => {
        try {
            const response = await API_POST(
                `${ENDPOINTS.GIG_POST_UNFILLED}/${encodeURIComponent(siteCd)}`,
                {}
            );
            return { siteCd, response: response as GigPostUnfilledResponse };
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to post unfilled gig shifts');
        }
    }
);

const gigPostUnfilledSlice = createSlice({
    name: 'gigPostUnfilled',
    initialState,
    reducers: {
        resetGigPostUnfilled: (state) => {
            state.lastSiteCd = null;
            state.lastResponse = null;
            state.loading = false;
            state.success = false;
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(postGigUnfilled.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(postGigUnfilled.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.lastSiteCd = action.payload.siteCd;
                state.lastResponse = action.payload.response;
            })
            .addCase(postGigUnfilled.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { resetGigPostUnfilled, clearError } = gigPostUnfilledSlice.actions;
export default gigPostUnfilledSlice.reducer;
